import { Box, styled, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { BackButton } from '../../components/BackButton'
import ContactCard from '../../components/ContactCard'
import { PageContainer } from '../../components/layout/PageContainer'
import { Loader, LoadingBox } from '../../components/Loader'
import TitleBox from '../../components/TitleBox'
import { TestIds } from '../../testIds'
import AuthSlice, { selectContactInfo } from '../azure-b2c/storage'
import { AgreementTitle } from './components/AgreementTitle'
import DeliveryDetails from './DeliveryDetails'
import SalesLine from './SalesLine'
import SalesOrders from './SalesOrders'
import {
  selectSalesAgreementById,
  selectAgreementsRequestStatus,
  selectAgreementAndAccountById,
} from './storage'
import { CONTACT_CARD_TITLE } from '../../util/consts'
import YesNoAction from '../../components/YesNoAction'
import { useAppDispatch } from '../../store/rootStore'
import { Button } from '../../components/Button'
import { useDrawdownModal } from '../drawdown/useDrawdownRequest'
import { getAvailableQuantity } from './utils/getAvailableQuantity'
import { usePreviousPage } from '../../util/usePreviousPage'

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    gap: '16px',
  },
}))

const SaleAgreementContainer = styled(Box)(() => ({}))

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '40px',
  [theme.breakpoints.down('sm')]: {
    gap: '32px',
  },
}))

export const SalesAgreement = () => {
  const params = useParams()

  const salesAgreementId = params.salesAgreementId

  const salesAgreement = useSelector(selectSalesAgreementById(salesAgreementId))
  const { agreement, account } = useSelector(selectAgreementAndAccountById(salesAgreementId))
  const requestStatus = useSelector(selectAgreementsRequestStatus)
  const relationshipOwner = useSelector(selectContactInfo)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { previousPage } = usePreviousPage()

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const drawdown = useDrawdownModal()

  const onRequestDelivery = React.useCallback(() => {
    if (salesAgreement) {
      drawdown.setAgreement(salesAgreement)
    }
  }, [salesAgreement, drawdown])

  const drawdownButton = React.useMemo(() => {
    const width = isSm ? '100%' : 'auto'
    if (
      account?.hasCheckoutAccess &&
      account?.hasShareholderAccess &&
      salesAgreement?.supportsDelivery &&
      salesAgreement?.status === 'Effective' &&
      getAvailableQuantity(salesAgreement) > 0
    ) {
      return (
        <Box alignSelf='flex-end' ml='auto' width={width}>
          <Button
            variant='contained'
            sx={{ width }}
            onClick={onRequestDelivery}
            data-testid={TestIds.feat.drawdown.requestButton}
          >
            Request delivery
          </Button>
        </Box>
      )
    }
  }, [isSm, onRequestDelivery, account, salesAgreement])

  // Only displays if sales agreement is valid
  const contactCard = React.useMemo(() => {
    if (salesAgreement) {
      return (
        <Wrapper>
          <ContactCard
            title={CONTACT_CARD_TITLE.underAgreementDetails}
            testId={TestIds.components.contactCard.card}
            {...relationshipOwner}
          />
          {<DeliveryDetails {...salesAgreement} />}
        </Wrapper>
      )
    }
  }, [relationshipOwner, salesAgreement])

  // Only displays if sales agreement is valid
  const memo = React.useMemo(() => {
    if (salesAgreement) {
      return (
        <SaleAgreementContainer>
          <ContentContainer>
            <AgreementTitle
              sxContainer={{ width: '100%', flexWrap: 'wrap', gap: '32px' }}
              title={salesAgreement.title ?? salesAgreement.id}
              category={salesAgreement.classification}
              status={salesAgreement.status}
              endDate={salesAgreement.endDate}
              startDate={salesAgreement.startDate}
            >
              {drawdownButton}
            </AgreementTitle>
            <SalesLine data={salesAgreement.lines} />
            <SalesOrders agreementId={salesAgreement.id} />
            {contactCard}
          </ContentContainer>
        </SaleAgreementContainer>
      )
    } else if (requestStatus === 'pending') {
      return (
        <LoadingBox>
          <Loader title='Loading details' />
        </LoadingBox>
      )
    } else if (account && agreement) {
      return (
        <Box mb={2}>
          <YesNoAction
            display='Sales agreement not available'
            body={
              <span>
                <span>Account: {account?.accountName}</span>
                <br />
                <span>Account number: {account?.accountNumber}</span>
                <br />
                <br />
                Looks like this Sales Agreement is associated with a different account. If you have
                other accounts with Farmlands you can switch using the account selector.
              </span>
            }
            optionA={{
              label: 'Switch back to ' + account.accountName,
              onClick: () => {
                dispatch(AuthSlice.actions.setSelectedFarmerAccount(account.accountNumber))
              },
            }}
            optionB={{
              label: 'Back to Sales Agreements',
              onClick: () => {
                navigate('/agreements')
              },
            }}
          />
        </Box>
      )
    } else {
      return (
        <>
          <TitleBox
            display='Sales Agreement not available'
            body='The Sales Agreement you are trying to view does not exist for this account.'
          />
          <Wrapper>
            <ContactCard
              title={'Contact a Farmlands representative to discuss Sales Agreements.'}
              testId={TestIds.components.contactCard.card}
              {...relationshipOwner}
            />
          </Wrapper>
        </>
      )
    }
  }, [salesAgreement, agreement, account, isSm, drawdownButton])

  return (
    <PageContainer data-testid={TestIds.pages.salesAgreement}>
      <BackButton
        text='All Sales Agreements'
        onClick={() => {
          if (previousPage === '/agreements') navigate(-1)
          else navigate('/agreements')
        }}
      />
      {memo}
    </PageContainer>
  )
}

export default SalesAgreement
